import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import Lightbox from '../components/Lightbox';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function Home() {
  const { lang, t, getLocalized } = useLanguage();
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const [selectedExp, setSelectedExp] = useState('');
  const [selectedGuests, setSelectedGuests] = useState('2');
  const [activeDestIndex, setActiveDestIndex] = useState(0);
  const testimonialsRef = useRef(null);

  // Dynamic scroll zoom for hero background
  const heroZoom = useScrollZoom({ baseScale: 1.15, maxScale: 1.45, zoomSpeed: 0.0006, parallaxSpeed: 0.2 });
  const ctaZoom = useScrollZoom({ baseScale: 1.05, maxScale: 1.3, zoomSpeed: 0.0004, parallaxSpeed: 0.15 });

  const scrollTestimonials = (direction) => {
    if (testimonialsRef.current) {
      const scrollAmount = direction === 'left' ? -380 : 380;
      testimonialsRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Fetch experiences from CMS v2 endpoint
    fetch('https://api.dev.auxo.dk/public/v2/content/experiences')
      .then((res) => res.json())
      .then((data) => {
        if (data.items) setExperiences(data.items);
      })
      .catch((err) => console.error('Error fetching experiences:', err));

    // Fetch main gallery
    fetch('https://api.dev.auxo.dk/public/v2/content/galleries')
      .then((res) => res.json())
      .then((data) => {
        if (data.items && data.items.length > 0) {
          const gen = data.items.find((g) => g.gallery_slug === 'general') || data.items[0];
          if (gen && gen.images) setGalleryImages(gen.images);
        }
      })
      .catch((err) => console.error('Error fetching galleries:', err));

    // Fetch testimonials
    fetch('https://api.dev.auxo.dk/public/v2/content/testimonials')
      .then((res) => res.json())
      .then((data) => {
        if (data.items) setTestimonials(data.items);
      })
      .catch((err) => console.error('Error fetching testimonials:', err));
  }, []);

  const openLightbox = (index) => {
    setActiveImgIndex(index);
    setLightboxOpen(true);
  };

  const handleHeroSearch = (e) => {
    e.preventDefault();
    if (selectedExp) {
      navigate(`/oplevelser/${selectedExp}`);
    } else {
      navigate('/oplevelser');
    }
  };

  const whyUsItems = t('whyUs.items') || [];
  const destinations = t('destinations.items') || [];

  return (
    <>
      <HeadMeta 
        title="Captain Hurghada Tours | Rejser & Oplevelser i Hurghada" 
        description="Din personlige rejsepartner i Hurghada. Kombination af dansk tryghed og lokal egyptisk ekspertise til uforglemmelige udflugter, bådture, dykning og ørkensafari."
      />

      {/* 1. HERO BANNER - Saad Travel Inspired Modern Aesthetic */}
      <section className="relative min-h-[640px] lg:min-h-[720px] flex items-center justify-center pt-28 sm:pt-36 lg:pt-40 pb-16 sm:pb-20 overflow-hidden bg-slate-900">
        {/* Background Image with Cloudflare Resizing & Scroll Zoom */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/hurghada-sunny-hero.jpg"
            alt="Captain Hurghada Tours Rødehavet"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover object-center"
          />
          {/* Lighter, balanced gradient overlays for a bright, sunny aesthetic while ensuring text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-slate-950/30 to-black/15 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-black/25 pointer-events-none" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl">
            {/* Top pill badge */}
            <ScrollReveal variant="up" duration={700} delay={50}>
              <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 backdrop-blur-md text-amber-300 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-6 shadow-sm">
                <i className="fa-solid fa-compass text-amber-400" aria-hidden="true" />
                <span>{t('hero.badge')}</span>
              </div>
            </ScrollReveal>

            {/* Display H1 Title */}
            <ScrollReveal variant="up" duration={800} delay={150}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15] mb-5 drop-shadow-md">
                {lang === 'da' ? (
                  <>
                    Din personlige rejsepartner i <span className="text-amber-400 drop-shadow-sm">Hurghada</span>
                  </>
                ) : (
                  <>
                    Your Personal Travel Partner in <span className="text-amber-400 drop-shadow-sm">Hurghada</span>
                  </>
                )}
              </h1>
            </ScrollReveal>

            {/* Subtitle */}
            <ScrollReveal variant="up" duration={800} delay={250}>
              <p className="text-base sm:text-lg text-slate-100 leading-relaxed mb-8 max-w-2xl font-medium drop-shadow-sm">
                {t('hero.subtitle')}
              </p>
            </ScrollReveal>

            {/* Hero CTAs - Pill Style */}
            <ScrollReveal variant="up" duration={800} delay={350}>
              <div className="flex flex-wrap items-center gap-4 mb-10">
                <Link
                  to="/oplevelser"
                  className="inline-flex items-center gap-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm sm:text-base px-7 py-3.5 rounded-full shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  <span>{t('hero.exploreTours') || (lang === 'da' ? 'Udforsk ture' : 'Explore Tours')}</span>
                  <i className="fa-solid fa-circle-arrow-right text-base" aria-hidden="true" />
                </Link>

                <Link
                  to="/planlaeg-din-rejse"
                  className="inline-flex items-center gap-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-md font-semibold text-sm sm:text-base px-6 py-3.5 rounded-full hover:scale-[1.02] active:scale-95 transition-all"
                >
                  <span>{t('hero.ctaPlan')}</span>
                  <i className="fa-solid fa-calendar-check text-amber-400" aria-hidden="true" />
                </Link>
              </div>
            </ScrollReveal>
          </div>

          {/* Quick Search & Filter Card - Elevated Floating Component */}
          <ScrollReveal variant="up" duration={800} delay={450}>
            <div className="mt-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-2xl max-w-4xl text-slate-900">
              <form onSubmit={handleHeroSearch} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                {/* Select Experience */}
                <div className="sm:col-span-6 lg:col-span-5">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                    <i className="fa-solid fa-location-dot text-amber-500 mr-1.5" aria-hidden="true" />
                    {t('heroSearch.experienceLabel')}
                  </label>
                  <select
                    value={selectedExp}
                    onChange={(e) => setSelectedExp(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-xs sm:text-sm rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none font-medium cursor-pointer"
                  >
                    <option value="">{t('heroSearch.experiencePlaceholder')}</option>
                    {experiences.map((exp) => {
                      const slug = getLocalized(exp.experience_slug) || exp.experience_slug || exp.slug?.da || exp.slug;
                      const title = getLocalized(exp.title);
                      return (
                        <option key={exp.id} value={slug}>
                          {title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Number of Guests */}
                <div className="sm:col-span-3 lg:col-span-3">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                    <i className="fa-solid fa-user-group text-amber-500 mr-1.5" aria-hidden="true" />
                    {t('heroSearch.guestsLabel')}
                  </label>
                  <select
                    value={selectedGuests}
                    onChange={(e) => setSelectedGuests(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-xs sm:text-sm rounded-xl py-2.5 px-3 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none font-medium cursor-pointer"
                  >
                    <option value="1">{t('heroSearch.person1') || '1 person'}</option>
                    <option value="2">{t('heroSearch.persons2') || '2 personer'}</option>
                    <option value="3-4">{t('heroSearch.persons34') || '3-4 personer'}</option>
                    <option value="5+">{t('heroSearch.persons5plus') || '5+ personer'}</option>
                  </select>
                </div>

                {/* Submit Search CTA Button */}
                <div className="sm:col-span-3 lg:col-span-4 sm:self-end">
                  <button
                    type="submit"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs sm:text-sm py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 shadow-md shadow-amber-500/20 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <i className="fa-solid fa-magnifying-glass text-xs" aria-hidden="true" />
                    <span>{t('heroSearch.searchBtn')}</span>
                  </button>
                </div>
              </form>

              {/* Trust Micro-Badges Row */}
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-[11px] sm:text-xs text-slate-600 font-medium">
                <div className="flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                  <span>{t('heroBadges.danishContact') || 'Dansktalende kontakt'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                  <span>{t('heroBadges.padiGuides') || 'Lokale PADI guider'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                  <span>{t('heroBadges.hotelPickup') || 'Hotel-afhentning inkluderet'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                  <span>{t('heroBadges.fixedPrices') || 'Faste priser & tryghed'}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 2. "WHY CHOOSE US" SECTION - Asymmetric Photo Collage + 2x3 Feature Card Grid */}
      <section className="py-20 lg:py-24 bg-white text-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header with Dual-Tone Heading */}
          <ScrollReveal variant="up" duration={700}>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-extrabold uppercase tracking-widest bg-amber-50 px-3.5 py-1.5 rounded-full mb-3">
                <i className="fa-solid fa-award" aria-hidden="true" />
                <span>{t('whyUs.qualityBadge')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {t('whyUs.title')}
              </h2>
              <p className="mt-3 text-slate-500 text-sm sm:text-base leading-relaxed">
                {t('whyUs.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column: Asymmetric Photo Collage (Saad Travel Signature) */}
            <div className="lg:col-span-5 relative">
              <ScrollReveal variant="left" duration={800}>
                <div className="relative mx-auto max-w-md lg:max-w-none">
                  
                  {/* Primary Large Image with organic rounded styling */}
                  <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white aspect-[4/3] bg-slate-100">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=650,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/orangebay.jpeg"
                      alt="Hurghada Orange Bay Sejlads"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <span className="text-xs font-semibold bg-amber-500 text-white px-2.5 py-0.5 rounded-full inline-block mb-1">
                        {t('whyUs.photoTag')}
                      </span>
                      <p className="text-sm font-bold drop-shadow">{t('whyUs.photoDesc')}</p>
                    </div>
                  </div>

                  {/* Secondary Overlapping Photo */}
                  <div className="absolute -bottom-8 -right-4 sm:-right-6 w-3/5 rounded-[1.75rem] overflow-hidden shadow-2xl border-4 border-white aspect-square bg-slate-900">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=450,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/nicoline-og-mand.webp"
                      alt="Nicoline og Ziad i Hurghada"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 text-white">
                      <p className="text-xs font-bold">{t('whyUs.expDanishLocal')}</p>
                      <p className="text-[10px] text-slate-200">{t('whyUs.foundedBy')}</p>
                    </div>
                  </div>

                  {/* Floating Trust Rating Badge */}
                  <div className="absolute -top-4 -left-3 sm:-left-5 bg-white rounded-2xl p-3 shadow-xl border border-slate-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center font-extrabold text-sm shadow-md shadow-amber-500/30">
                      ★ 5.0
                    </div>
                    <div>
                      <div className="flex text-amber-400 text-xs">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                        ))}
                      </div>
                      <p className="text-[11px] font-bold text-slate-800 mt-0.5">{t('whyUs.topReviewed')}</p>
                    </div>
                  </div>

                </div>
              </ScrollReveal>
            </div>

            {/* Right Column: 2x3 Grid of Feature Cards (Saad Travel Specification) */}
            <div className="lg:col-span-7 pt-6 lg:pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyUsItems.map((item, idx) => (
                  <ScrollReveal key={idx} variant="up" delay={idx * 80} duration={650}>
                    <div className="border border-slate-200/90 rounded-xl p-4 sm:p-5 flex items-start gap-4 bg-white shadow-sm hover:shadow-md hover:border-amber-400/50 hover:bg-slate-50/50 transition-all group h-full">
                      <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white flex items-center justify-center text-lg flex-shrink-0 transition-colors shadow-sm">
                        <i className={item.icon || 'fa-solid fa-check'} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm sm:text-base font-bold text-slate-900 group-hover:text-amber-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs text-slate-500 leading-relaxed mt-1">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 3. POPULAR TOURS / EXPERIENCES SECTION - 4-Column Saad Travel Card Grid */}
      <section className="py-20 lg:py-24 bg-slate-50 text-slate-900 relative border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <ScrollReveal variant="up" duration={700}>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-extrabold uppercase tracking-widest bg-amber-100/70 px-3.5 py-1 rounded-full mb-2">
                  <i className="fa-solid fa-fire text-amber-500" aria-hidden="true" />
                  <span>Udforsk Vores Ture</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                  {lang === 'da' ? (
                    <>
                      Populære <span className="text-amber-500">Oplevelser</span>
                    </>
                  ) : (
                    <>
                      Popular <span className="text-amber-500">Tours</span>
                    </>
                  )}
                </h2>
              </div>
              <Link
                to="/oplevelser"
                className="inline-flex items-center gap-2 text-slate-700 hover:text-amber-600 font-bold text-sm group transition-colors self-start md:self-auto"
              >
                <span>{t('experiences.viewAll')}</span>
                <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </ScrollReveal>

          {/* 4-Card Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.slice(0, 8).map((exp, index) => {
              const slug = getLocalized(exp.experience_slug) || exp.experience_slug || exp.slug?.da || exp.slug;
              const title = getLocalized(exp.title);
              const subtitle = getLocalized(exp.subtitle);
              const cardImg = exp.card_image?.[0] || 'https://assets.auxodev.dk/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/hero-redsea.jpg';
              const price = getLocalized(exp.price_from);
              const duration = getLocalized(exp.duration);

              return (
                <ScrollReveal 
                  key={exp.id} 
                  variant="up" 
                  delay={index * 100} 
                  duration={700}
                >
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col justify-between shadow-sm hover:shadow-xl hover:border-amber-400/60 transition-all duration-300 group h-full">
                    <div>
                      {/* Image Top (16:10 Ratio) */}
                      <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${cardImg.replace('https://assets.auxodev.dk/', '')}`}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

                        {/* Top Badges */}
                        {index === 0 && (
                          <div className="absolute top-2.5 left-2.5 bg-orange-500 text-white text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md shadow-md">
                            {lang === 'da' ? 'Anbefalet' : 'Recommended'}
                          </div>
                        )}
                        {index === 1 && (
                          <div className="absolute top-2.5 left-2.5 bg-amber-500 text-white text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md shadow-md">
                            {lang === 'da' ? 'Bestseller' : 'Popular'}
                          </div>
                        )}
                      </div>

                      {/* Content Body */}
                      <div className="p-4 flex-1 flex flex-col">
                        {/* Star Rating Row */}
                        <div className="flex items-center gap-1 text-amber-400 text-xs mb-1.5">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                          ))}
                          <span className="text-slate-400 text-[11px] font-medium ml-1">(5.0)</span>
                        </div>

                        {/* Card Title */}
                        <h3 className="text-base font-bold text-slate-900 group-hover:text-amber-600 transition-colors line-clamp-1">
                          {title}
                        </h3>

                        {/* Subtitle / Description */}
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                          {subtitle}
                        </p>

                        {/* Metadata Row */}
                        <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-slate-700">
                          {price && (
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-400 font-normal">{t('experiences.fromPrice')}</span>
                              <span className="text-amber-600 font-bold text-sm">{price}</span>
                            </div>
                          )}
                          {duration && (
                            <div className="flex items-center gap-1.5 text-slate-500 text-[11px]">
                              <i className="fa-regular fa-clock text-amber-500" aria-hidden="true" />
                              <span>{duration}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card CTA Button (Saad Travel Style: Solid amber rectangular button) */}
                    <div className="p-4 pt-0">
                      <Link
                        to={`/oplevelser/${slug}`}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all text-center"
                      >
                        <span>{t('experiences.readMore')}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Centered "View More ->" Action Pill */}
          <ScrollReveal variant="up" duration={700} delay={200}>
            <div className="mt-12 text-center">
              <Link
                to="/oplevelser"
                className="inline-flex items-center gap-2.5 bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-md shadow-amber-500/20 hover:shadow-lg transition-all"
              >
                <span>{t('experiences.viewAll')}</span>
                <i className="fa-solid fa-circle-arrow-right text-base" aria-hidden="true" />
              </Link>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* 4. "POPULAR DESTINATION" SECTION - Staggered Visual Showcase Cards */}
      <section className="py-20 lg:py-24 bg-white text-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <ScrollReveal variant="up" duration={700}>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-extrabold uppercase tracking-widest bg-amber-50 px-3.5 py-1 rounded-full mb-2">
                <i className="fa-solid fa-map-location-dot text-amber-500" aria-hidden="true" />
                <span>{t('destinations.sectionTag')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {t('destinations.title')}
              </h2>
              <p className="mt-3 text-slate-500 text-sm sm:text-base leading-relaxed">
                {t('destinations.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          {/* 4 Large Destination Showcase Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {destinations.map((dest, i) => (
              <ScrollReveal key={i} variant="up" delay={i * 100} duration={700}>
                <Link
                  to={dest.link || '/oplevelser'}
                  className="group relative block aspect-[3/4] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 bg-slate-950 border border-slate-100"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${dest.image.replace('https://assets.auxodev.dk/', '')}`}
                    alt={dest.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  {/* Subtle dark gradient scrim */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent" />

                  {/* Destination Content Overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-5 flex flex-col justify-end">
                    <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1">
                      {dest.tag}
                    </span>
                    <h3 className="text-xl font-extrabold text-white group-hover:text-amber-400 transition-colors mb-2">
                      {dest.name}
                    </h3>
                    <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed mb-4 opacity-90">
                      {dest.desc}
                    </p>

                    {/* Bottom CTA Pill */}
                    <div className="flex items-center justify-between pt-3 border-t border-white/15 text-xs text-white font-semibold">
                      <span>{t('destinations.viewDestination')}</span>
                      <span className="w-8 h-8 rounded-full bg-white/20 group-hover:bg-amber-500 text-white flex items-center justify-center transition-colors">
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

        </div>
      </section>

      {/* 5. "HOW IT WORKS" SECTION - 3 Simple Steps */}
      <section className="py-20 lg:py-24 bg-slate-50 text-slate-900 relative border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <ScrollReveal variant="up" duration={700}>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {t('howItWorks.title')}
              </h2>
              <p className="mt-3 text-slate-500 text-sm sm:text-base leading-relaxed">
                {t('howItWorks.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Step 1 */}
            <ScrollReveal variant="up" delay={100} duration={700}>
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white font-extrabold text-2xl flex items-center justify-center shadow-lg shadow-amber-500/25 mb-5">
                  1
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">
                  {t('howItWorks.step1Title')}
                </h3>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                  {t('howItWorks.step1Desc')}
                </p>
              </div>
            </ScrollReveal>

            {/* Step 2 */}
            <ScrollReveal variant="up" delay={250} duration={700}>
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white font-extrabold text-2xl flex items-center justify-center shadow-lg shadow-amber-500/25 mb-5">
                  2
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">
                  {t('howItWorks.step2Title')}
                </h3>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                  {t('howItWorks.step2Desc')}
                </p>
              </div>
            </ScrollReveal>

            {/* Step 3 */}
            <ScrollReveal variant="up" delay={400} duration={700}>
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-amber-500 text-white font-extrabold text-2xl flex items-center justify-center shadow-lg shadow-amber-500/25 mb-5">
                  3
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">
                  {t('howItWorks.step3Title')}
                </h3>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                  {t('howItWorks.step3Desc')}
                </p>
              </div>
            </ScrollReveal>
          </div>

        </div>
      </section>

      {/* 6. PHOTO GALLERY SECTION */}
      <section className="py-20 lg:py-24 bg-white text-slate-900 relative border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <ScrollReveal variant="up" duration={700}>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {t('gallery.title')}
              </h2>
              <p className="mt-3 text-slate-500 text-sm sm:text-base leading-relaxed">
                {t('gallery.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((imgUrl, index) => (
              <ScrollReveal
                key={index}
                variant="scale"
                delay={(index % 4) * 100}
                duration={650}
              >
                <div
                  onClick={() => openLightbox(index)}
                  className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group border border-slate-200 shadow-sm hover:shadow-xl transition-all"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`Hurghada galleri ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300" aria-hidden="true" />
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

        </div>
      </section>

      {/* 7. REVIEWS SECTION - Modern Card Carousel */}
      <section className="py-20 lg:py-24 bg-slate-50 text-slate-900 relative border-t border-slate-200/60 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <ScrollReveal variant="up" duration={700}>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-extrabold uppercase tracking-widest bg-amber-100/70 px-3.5 py-1 rounded-full mb-2">
                  <i className="fa-solid fa-star text-amber-500" aria-hidden="true" />
                  <span>{t('reviews.sectionTag')}</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                  {t('reviews.title')}
                </h2>
              </div>

              {/* Navigation Arrows */}
              <div className="flex items-center gap-3 self-start md:self-auto">
                <button
                  onClick={() => scrollTestimonials('left')}
                  aria-label="Forrige anmeldelse"
                  className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all active:scale-95 cursor-pointer shadow-sm"
                >
                  <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
                </button>
                <button
                  onClick={() => scrollTestimonials('right')}
                  aria-label="Næste anmeldelse"
                  className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all active:scale-95 cursor-pointer shadow-sm"
                >
                  <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
                </button>
              </div>
            </div>
          </ScrollReveal>

          {/* Testimonial Cards Slider */}
          <ScrollReveal variant="up" delay={200} duration={800}>
            <div className="relative">
              <div
                ref={testimonialsRef}
                className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-4 pt-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {testimonials.map((tItem) => {
                  const titleText = getLocalized(tItem.title)?.replace(/^"+|"+$/g, '');
                  const reviewHtml = getLocalized(tItem.review);
                  const author = tItem.author;
                  const locationText = getLocalized(tItem.location)?.replace(/^"+|"+$/g, '');

                  return (
                    <div
                      key={tItem.id}
                      className="w-full sm:w-[350px] lg:w-[380px] flex-shrink-0 snap-start bg-white rounded-2xl p-7 border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                    >
                      <div>
                        {/* Stars */}
                        <div className="flex items-center gap-1 text-amber-400 mb-4 text-base">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                          ))}
                        </div>

                        {/* Review Title */}
                        {titleText && (
                          <h3 className="font-bold text-slate-900 text-base mb-2 leading-snug">
                            {titleText}
                          </h3>
                        )}

                        {/* Review text */}
                        <div 
                          className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-6 italic line-clamp-4"
                          dangerouslySetInnerHTML={{ __html: reviewHtml }}
                        />
                      </div>

                      {/* Author */}
                      <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
                          {author?.charAt(0) || 'G'}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-sm">
                            {author}
                          </h4>
                          <p className="text-[11px] text-slate-400">
                            {locationText || 'Danmark'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* 8. FINAL CTA BANNER */}
      <section className="py-20 sm:py-24 relative overflow-hidden text-white bg-slate-950">
        {/* Background Image with Dynamic Scroll Zoom */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/orangebay.jpeg"
            alt="Captain Hurghada Tours Strand"
            style={{
              transform: `scale(${ctaZoom.scale}) translate3d(0, ${ctaZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60 pointer-events-none" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollReveal variant="up" duration={700}>
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 text-amber-300 px-4 py-1.5 rounded-full text-xs font-bold mb-4 uppercase tracking-wider">
              <i className="fa-solid fa-plane-departure" aria-hidden="true" />
              <span>{t('ctaBanner.readyBadge') || (lang === 'da' ? 'Klar til eventyr?' : 'Ready for adventure?')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 tracking-tight text-white leading-tight">
              {t('ctaBanner.title')}
            </h2>
          </ScrollReveal>
          
          <ScrollReveal variant="up" delay={150} duration={700}>
            <p className="text-base sm:text-lg text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed font-normal">
              {t('ctaBanner.subtitle')}
            </p>
          </ScrollReveal>

          <ScrollReveal variant="scale" delay={300} duration={700}>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-base hover:scale-105 transition-all shadow-xl shadow-amber-500/30"
            >
              <span>{t('ctaBanner.button')}</span>
              <i className="fa-solid fa-circle-arrow-right text-lg" aria-hidden="true" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={galleryImages}
        currentIndex={activeImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setActiveImgIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setActiveImgIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </>
  );
}
